import { Route, Routes } from 'react-router-dom';
import { Mode, applyMode } from '@cloudscape-design/global-styles';
import { useEffect, useMemo } from 'react';

import HomePage from './pages/home';
import ErrorPage from './pages/error';
import ScenarioPage from './pages/scenarios/scenario';
import LeaderboardPage from './pages/scenarios/scenario/leaderboard';
import RoomPage from './pages/rooms/room';
import EventsPage from './pages/manage/events';
import EventPage from './pages/manage/events/event';
import EventScenarioPage from './pages/manage/scenario';
import ManagePage from './pages/manage';
import '@cloudscape-design/global-styles/index.css';
import './global.css';
import { ScrollToTop } from './components/ScrollToTop';
import { AutoUserSetup } from './components/user/AutoUserSetup';
import { LogtoRequiredLayout } from './common/auth/logto-layout';
import { LogtoSessionProvider, useLogtoSession } from './common/auth/logto-session';
import { UserProvider } from './components/user/UserContext';

import { BrowserRouter } from 'react-router-dom';
import { I18nProvider } from '@cloudscape-design/components/i18n';
import enMessages from '@cloudscape-design/components/i18n/messages/all.en';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { getErrorDetails } from './components/chat/ErrorTurn';
import { AxiosError } from 'axios';
import { liveApiUrl } from './common/api/axiosInstance';

Sentry.init({
  dsn: 'https://cc16bea16c2a61478d5544d64842e2a1@o4507625924722688.ingest.us.sentry.io/4509078692298752',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: process.env.NODE_ENV === 'development' ? 0.0 : 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', liveApiUrl],
  // Session Replay
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: process.env.NODE_ENV === 'development' ? 0.0 : 1.0,
  environment: process.env.NODE_ENV === 'development' ? 'development' : 'production',
  enabled: process.env.NODE_ENV === 'development' ? false : true,
});

function NavRoutes() {
  useEffect(() => {
    applyMode(Mode.Dark);
  }, []);

  const { isAuthenticated, user } = useLogtoSession();

  // todo use user code info as well here if available
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }
    const sentryUserDetails = {
      id: user.subject,
      email: user?.email,
      name: user?.email,
    };
    Sentry.setUser(sentryUserDetails);
  }, [isAuthenticated, user]);

  return (
    <Routes>
      {/* External App (UserProvider) */}
      <Route>
        <Route path="/scenarios">
          <Route
            element={
              <AutoUserSetup>
                <ScenarioPage />
              </AutoUserSetup>
            }
            path=":scenarioUuid"
          />
          <Route element={<LeaderboardPage />} path=":scenarioUuid/leaderboard" />
        </Route>
        <Route path="/rooms">
          <Route
            element={
              <AutoUserSetup>
                <RoomPage />
              </AutoUserSetup>
            }
            path=":roomUuid"
          />
        </Route>
      </Route>

      {/* Internal Logto session app (protected routes) */}
      <Route element={<LogtoRequiredLayout />}>
        <Route path="/manage">
          <Route element={<ManagePage />} index />
          <Route path="events">
            <Route index element={<EventsPage />} />
            <Route path=":eventUuid" element={<EventPage />} />
          </Route>
          <Route path="scenarios">
            <Route path=":scenarioUuid" element={<EventScenarioPage />} />
          </Route>
        </Route>
      </Route>

      {/* Public App */}
      <Route element={<HomePage />} path="/" />
      <Route element={<ErrorPage />} path="*" />
    </Routes>
  );
}

function AppWithUserContext() {
  const { isAuthenticated, logout } = useLogtoSession();

  const queryClient = useMemo(() => {
    return new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
      queryCache: new QueryCache({
        onError: async (error, query) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 401 &&
            isAuthenticated
          ) {
            await logout({ returnTo: window.location.href, redirectToLogin: true });
            return;
          }
          const { title, description } = getErrorDetails(error);
          // Report the error to Sentry
          Sentry.captureException(error, {
            tags: {
              queryKey: `${query.queryKey}`,
              title,
              description,
            },
          });
        },
      }),
      mutationCache: new MutationCache({
        onError: async (error) => {
          if (
            error instanceof AxiosError &&
            error.response &&
            error.response.status === 401 &&
            isAuthenticated
          ) {
            await logout({ returnTo: window.location.href, redirectToLogin: true });
            return;
          }
          const { title, description } = getErrorDetails(error);
          // Report the error to Sentry
          Sentry.captureException(error, {
            tags: {
              title,
              description,
            },
          });
        },
      }),
    });
  }, [isAuthenticated, logout]);

  return (
    <QueryClientProvider client={queryClient}>
      <ScrollToTop />
      <NavRoutes />
    </QueryClientProvider>
  );
}

function App() {
  // TODO: Add a custom sentry fallback component
  return (
    <BrowserRouter>
      <I18nProvider locale="en" messages={[enMessages]}>
        <LogtoSessionProvider>
          <UserProvider>
            <AppWithUserContext />
          </UserProvider>
        </LogtoSessionProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
